import * as React from 'react';
import classnames from 'classnames';
import { get } from 'lodash';

import { getCloudinaryImageUrl } from 'utils/cloudinary';

import type { Image } from 'types';

type Props = {
  alt: string,
  image: Image | undefined | null,
  width: number,
  height?: number,
  preserveAspectRatio?: boolean,
  resizable: boolean,
  className?: string
};

type ImgRef = {
  current: null | HTMLImageElement
};

class CloudinaryImage extends React.PureComponent<Props> {
  static defaultProps = {
    resizable: false,
    alt: ''
  };

  imgRef: ImgRef = React.createRef();

  componentDidUpdate(prevProps: Props): void {
    const currentImageUrl = get(this.props, 'image.url', null);
    const prevImageUrl = get(prevProps, 'image.url', null);
    const imgElement = this.imgRef && this.imgRef.current || null;

    if (currentImageUrl !== prevImageUrl && imgElement) {
      imgElement.classList.remove('lazyloaded');
      imgElement.classList.add('lazyload');
    }
  }

  render(): React.ReactNode {
    const { image, width, height, alt, preserveAspectRatio, resizable, className } = this.props;

    if (!image || !image.url) {
      return null;
    }

    const roundedWidth = Math.ceil(width);
    const roundedHeight = height ? Math.ceil(height) : 0;
    const imageProps: { width?: string, height?: string | number } = {
      width: undefined,
      height: undefined
    };

    if (resizable) {
      imageProps.width = '100%';
    } else {
      imageProps.width = `${roundedWidth}px`;

      if (!preserveAspectRatio) {
        imageProps.height = roundedHeight && `${roundedHeight}px`;
      }
    }

    const imgClasses = classnames({
      lazyload: true,
      [`${String(className)}`]: Boolean(className)
    });

    return (
      <img
        ref={this.imgRef}
        data-src={getCloudinaryImageUrl(image, { width: roundedWidth, height: roundedHeight, preserveAspectRatio })}
        alt={alt}
        className={imgClasses}
        {...imageProps}
        data-hj-suppress
      />
    );
  }
}

export default CloudinaryImage;
